import axios, { AxiosResponse, AxiosRequestConfig } from 'axios'
import { getApiUrl, getMatrixApiUrl as getConfigMatrixApiUrl } from '@/lib/config'
import { getAuthScheme, type LoginType } from '@/lib/auth'
import { DEFAULT_LOGIN_ROUTE, noLoginCode } from "@/lib/constants";
import { clearUnderscoreCookies } from '@/lib/utils/clear-cookies'

async function getMatrixApiUrl(): Promise<string> {
  const specializedUrl = await getConfigMatrixApiUrl()
  if (specializedUrl) return specializedUrl
  return await getApiUrl()
}

const axiosInstance = axios.create({
  timeout: 300000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false,
})

axiosInstance.interceptors.request.use(async (config) => {
  clearUnderscoreCookies()

  if (!config.baseURL) {
    config.baseURL = await getMatrixApiUrl()
  }

  if (typeof window !== 'undefined') {
    const authStorage = localStorage.getItem('auth-storage')
    if (authStorage) {
      try {
        const { state } = JSON.parse(authStorage)
        if (state?.token) {
          const loginType = (state.loginType ?? 'local') as LoginType
          config.headers.Authorization = `${getAuthScheme(loginType)} ${state.token}`
        }
      } catch (error) {
        console.error('Error parsing auth storage:', error)
      }
    }
  }

  if (config.data instanceof FormData) {
    delete config.headers['Content-Type']
  } else if (config.method && ['post', 'put', 'patch'].includes(config.method.toLowerCase())) {
    config.headers['Content-Type'] = 'application/json'
  }

  return config
})

axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
      const data = response.data
      const payload = data?.data ?? data
      const success = data?.success || data?.code === 0 || data?.errcode === 0 || data?.code === undefined
      const code = data?.code;
      if (success) {
          return payload
      }
      if (noLoginCode.includes(code)) {
          localStorage.removeItem('auth-storage')
          window.location.href = DEFAULT_LOGIN_ROUTE
      }
      const message = data?.debug_msg || data?.msg || 'Request failed'
      return Promise.reject(new Error(message))
  },
  (error) => {
    return Promise.reject(error)
  }
)

export const matrixApiClient = axiosInstance as unknown as {
  get<T = unknown, R = T, D = unknown>(
    url: string,
    config?: AxiosRequestConfig<D>
  ): Promise<R>
  post<T = unknown, R = T, D = unknown>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig<D>
  ): Promise<R>
  put<T = unknown, R = T, D = unknown>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig<D>
  ): Promise<R>
  delete<T = unknown, R = T, D = unknown>(
    url: string,
    config?: AxiosRequestConfig<D>
  ): Promise<R>
  patch<T = unknown, R = T, D = unknown>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig<D>
  ): Promise<R>
  postForm<T = unknown, R = T, D = unknown>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig<D>
  ): Promise<R>
  putForm<T = unknown, R = T, D = unknown>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig<D>
  ): Promise<R>
  patchForm<T = unknown, R = T, D = unknown>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig<D>
  ): Promise<R>
}
